import apiService from "@/../api/rest";
import CheckoutDTO from "@/../api/data-transfer-objects/CheckoutDTO";
import {
  ERROR_PROCESSING_PPI_PAYMENT,
  ERROR_PROCESSING_PPI_PAYMENTS,
  ERROR_PROCESSING_PPI_PAYMENT_INSUFFICIENT_FUNDS,
  SENTRY_ERRORS
} from "@/../app-constants";
import errorLoggerAdapter from "@/adapters/errorLoggerAdapter";
import { cloneDeep } from "lodash";

// initial state
export const state = {
  processedAmenities: {},
  urlPassTender: {
    paymentType: "",
    subpaymentType: "",
    credential: ""
  }
};

// getters
const getters = {
  getProcessedAmenities(state) {
    return state.processedAmenities || {};
  },
  getProcessedAmenitiesApplied(state) {
    const amenitiesApplied = cloneDeep(state.processedAmenities) || {};

    // Filter out amenities with 0 amount
    if (amenitiesApplied?.resultPayments) {
      amenitiesApplied.resultPayments = amenitiesApplied.resultPayments.filter((payment) => {
        if (!payment) return false;

        // We know it's PPI, they also have the balance prop.
        if (payment.appliedAmenity > 0) return true;

        // checking to make sure it's not a PPI and then checks its balance value.
        return Object.keys(payment).indexOf("appliedAmenity") === -1 && payment.balance > 0;
      });
    }

    return amenitiesApplied;
  },
  getURLpassTender(state, getters, rootState, rootGetters) {
    const tenders = rootGetters["vendor/getTenders"];
    const { paymentType, subpaymentType } = state.urlPassTender;
    const tenderMatch = tenders.find(
      (tender) => "" + tender.paymentType === "" + paymentType && "" + tender.subpaymentType === "" + subpaymentType
    );

    return {
      ...state.urlPassTender,
      name: tenderMatch?.name || "",
      buttonText: tenderMatch?.buttonText || ""
    };
  }
};

// actions
const actions = {
  async voidAmenityPayments({ getters, rootGetters }, _payments = null) {
    const payments = _payments || getters["getProcessedAmenities"]?.resultPayments || [];
    const accountId = rootGetters["vendor/getActiveVendor"].defaultMobileOrderingAccountID;
    const OAPICart = rootGetters["cart/getOAPICart"];

    const result = payments.map((payment) => {
      if (payment && payment.appliedAmenity) {
        return apiService.voidPayment(
          accountId,
          payment.selectedTender.paymentType,
          payment.selectedTender.subpaymentType,
          CheckoutDTO.getVoidPaymentPayload(payment, OAPICart, rootGetters)
        );
      }
    });

    return await Promise.all(result);
  },

  /**
   * OAPI ONLY: Sends each selected payment to process endpoint. Processed payments, and amenities are returned.
   * CC checkout payment will be added back as the first element of the array if it's present.
   * @param rootGetters
   * @param paymentData
   * @returns {Promise<*>}
   */
  async processAmenityPayments({ rootGetters, dispatch, commit }, paymentData) {
    const appliedAmenities = rootGetters["cart/getAppliedAmenities"];
    const accountId = rootGetters["vendor/getActiveVendor"].defaultMobileOrderingAccountID;
    const OAPICart = rootGetters["cart/getOAPICart"];
    const reportedAmenities = [];
    let resultPayments = [];
    return new Promise(async (resolve, reject) => {
      if (!paymentData || !paymentData.length) {
        resolve({});
        return;
      }

      for (const amenity of appliedAmenities?.amenities) {
        try {
          if (amenity.appliedAmenity > 0) {
            const processResponse = await apiService.processPayment(
              accountId,
              amenity.selectedTender.paymentType,
              amenity.selectedTender.subpaymentType,
              CheckoutDTO.getProcessPaymentPayload(amenity, OAPICart, rootGetters, reportedAmenities)
            );

            // check for success
            if (processResponse?.data?.success) {
              // get payments from response that only have a spent value. They were actually used to pay for items.
              const payments = [].concat(processResponse?.data?.payments.filter((filteredPayment) => filteredPayment?.spent > 0));

              // add processed payment data to original amenity payment.
              const processedPayments = payments.map((payment) => {
                return {
                  ...amenity,
                  buttonText: amenity?.selectedTender?.buttonText || "",
                  cardNumber: amenity?.card || "",
                  processedPayment: payment,
                  provider: processResponse?.headers?.provider
                };
              });

              if (processedPayments && processedPayments.length) {
                resultPayments = resultPayments.concat(processedPayments) || [];
              }

              const totalSpent = payments.reduce((acc, payment) => {
                return acc + +payment.spent;
              }, 0);

              // If total spent is less than the applied amenity, the user has an unpaid balance in their cart.
              // We adjust the payment to the total spent and void any payments that were processed.  We will send
              // them back to the payment screen to pay the remaining balance displaying an error message indicating
              // that they have an unpaid balance.
              if (amenity.appliedAmenity > totalSpent) {
                dispatch("voidAmenityPayments", resultPayments);

                commit("spinner/requestStop", null, { root: true });
                dispatch("updatePPIAmenity", { id: amenity.id, amenity: { adjustedAmount: totalSpent } });

                const paymentName = amenity?.selectedTender?.buttonText || amenity?.selectedTender?.name || "Card";
                const cardNumber = amenity?.card?.substring(amenity?.card?.length - 4) || "****";

                reject(`${ERROR_PROCESSING_PPI_PAYMENT_INSUFFICIENT_FUNDS(paymentName, cardNumber)}`);
                break;
              }

              reportedAmenities.push({
                card: amenity.card,
                selection: amenity.selectedTender.selection,
                selectionType: amenity.selectedTender.selectionType,
                paymentType: amenity.selectedTender.paymentType,
                subpaymentType: amenity.selectedTender.subpaymentType,
                appliedAmenity: amenity.appliedAmenity,
                payments: processResponse.data.payments || [],
                autoApplied: amenity.selectedTender.autoApplied
              });
            } else {
              // voids out any previously processed payments
              dispatch("voidAmenityPayments", resultPayments);

              commit("spinner/requestStop", null, { root: true });
              dispatch("removePPIAmenity", { id: amenity.id });

              const paymentName = amenity?.selectedTender?.buttonText || amenity?.selectedTender?.name || "Card";
              const cardNumber = amenity?.card?.substring(amenity?.card?.length - 4) || "****";
              let errorMessage = processResponse?.data.error ?? "";

              /**
               * This code is to parse through an error message on the CBORD side.
               * A sample message is "UINVALID ARG|amount:Amount must be less than 10,000"
               * This parsing logic would instead display "Amount must be less than 10,000"
               *
               * If the "|" doesn't exist, it will just display what came in the response as the error message.
               */
              const delimeter = errorMessage.indexOf("|");
              if (delimeter !== -1) {
                errorMessage = errorMessage.substring(delimeter + 1);
                const error = errorMessage.indexOf(":");
                if (error !== -1) {
                  errorMessage = errorMessage.substring(error + 1);
                }
              }

              reject(`${ERROR_PROCESSING_PPI_PAYMENT(paymentName, cardNumber)} ${errorMessage}`);
              break;
            }
          }
        } catch (e) {
          errorLoggerAdapter.createIssue(SENTRY_ERRORS.ERROR_OAPI_PROCESS_AMENITY, "PPI_amenities", "processAmenityPayments", {
            description: e
          });
          dispatch("voidAmenityPayments", resultPayments);
          commit("spinner/requestStop", null, { root: true });
          reject(ERROR_PROCESSING_PPI_PAYMENTS);
        }
      }

      // check for CC or AP payment and push back to payments
      const otherPayment = paymentData.find((payment) => !payment.appliedAmenity);
      if (otherPayment) resultPayments.splice(0, 0, otherPayment);

      commit("spinner/requestStop", null, { root: true });
      commit("setProcessedAmenities", { resultPayments, reportedAmenities });

      resolve({ resultPayments, reportedAmenities });
    });
  },

  saveURLTender({ commit }, urlTender) {
    commit("setURLTender", urlTender);
  },

  resetURLTender({ commit }) {
    commit("resetURLTender");
  }
};

// mutations
const mutations = {
  setProcessedAmenities(state, value) {
    state.processedAmenities = value;
  },
  clearProcessedAmenities(state) {
    state.processedAmenities = {};
  },
  setURLTender(state, urlTender) {
    state.urlPassTender = {
      ...state.urlPassTender,
      ...urlTender
    };
  },
  resetURLTender(state) {
    state.urlPassTender = {
      paymentType: "",
      subpaymentType: "",
      credential: ""
    };
  }
};

export default {
  namespaced: false,
  state,
  getters,
  actions,
  mutations
};
